import {
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid2,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  FilterList,
  VisibilityOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
  BarChartOutlined,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { getFacilitiyV2, getStatePersonalizedV2 } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Recharts Components
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';

export default function SectorReport({ props }) {
  const theme = useTheme();
  const reportRef = useRef(null);
  const chartRef = useRef(null);

  // Tab State: 0 = Full Report View, 1 = Graphical Overview
  const [activeTab, setActiveTab] = useState(0);

  // Control state variables
  const initState = { stateId: '', stateName: '' };
  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateSelected, setStateSelected] = useState(initState);

  // Data loading state
  const [facilityList, setFacilityList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10000;

  const CATEGORY_KEYS = ['SUPER', 'LARGE', 'MEDIUM', 'SMALL', 'MINI'];
  const CHART_COLORS = [
    '#1b5e20',
    '#ed6c02',
    '#2e7d32',
    '#0288d1',
    '#9c27b0',
    '#d32f2f',
    '#009688',
  ];

  useEffect(() => {
    fetchStateList();
  }, []);

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setStateList(data || []);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: error?.data?.result?.status?.desc || 'Error fetching states.',
      });
    } finally {
      setStateLoading(false);
    }
  };

  const handlePreviewReport = async () => {
    if (!stateSelected?.stateId) {
      openNotification({ type: 'warning', message: 'Please select a state first.' });
      return;
    }

    setLoading(true);
    try {
      const res = await getFacilitiyV2(
        '',
        '',
        '',
        '',
        stateSelected?.stateId || '',
        '',
        '',
        '',
        pageNum,
        pageSize
      );

      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setFacilityList(data || []);
      } else {
        openNotification({
          type: 'error',
          message: status?.desc || 'Failed to fetch facility data',
        });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.data?.status?.desc || 'Error fetching data';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setLoading(false);
    }
  };

  // Build aggregated matrix data from facility dataset
  const getSectorMatrix = () => {
    const matrix = {};

    facilityList.forEach((fac) => {
      const sector = fac.sectorName?.trim() || 'UNSPECIFIED SECTOR';
      const catRaw = fac.facilityCategoryName?.trim()?.toUpperCase() || 'OTHER';

      if (!matrix[sector]) {
        matrix[sector] = {
          sectorName: sector,
          total: 0,
          SUPER: 0,
          LARGE: 0,
          MEDIUM: 0,
          SMALL: 0,
          MINI: 0,
        };
      }

      matrix[sector].total += 1;
      if (CATEGORY_KEYS.includes(catRaw)) {
        matrix[sector][catRaw] += 1;
      }
    });

    return Object.values(matrix);
  };

  // Sector Pie Chart Data
  const getOverallPieData = () => {
    const matrix = getSectorMatrix();
    return matrix.map((row) => ({
      name: row.sectorName,
      value: row.total,
    }));
  };

  // Category Breakdown Pie Data for a single sector
  const getSectorCategoryPieData = (sectorRow) => {
    return CATEGORY_KEYS.map((catKey) => ({
      name: catKey,
      value: sectorRow[catKey] || 0,
    })).filter((item) => item.value > 0);
  };

  // Export Active Tab to Multipage PDF
  const exportToPDF = async () => {
    const targetElement = activeTab === 0 ? reportRef.current : chartRef.current;
    if (!targetElement) return;

    try {
      setLoading(true);

      const canvas = await html2canvas(targetElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        onclone: (clonedDoc) => {
          if (activeTab === 0) {
            const clonedReport = clonedDoc.querySelector('#report-pdf-container');
            if (clonedReport) {
              clonedReport.style.padding = '15px';
              const titles = clonedReport.querySelectorAll('.report-title');
              titles.forEach((t) => (t.style.fontSize = '15px'));
              const tableCells = clonedReport.querySelectorAll('.MuiTableCell-root');
              tableCells.forEach((cell) => {
                cell.style.fontSize = '10px';
                cell.style.padding = '4px 6px';
              });
            }
          }
        },
      });

      const imgWidth = 210; // A4 Width in mm
      const pageHeight = 297; // A4 Height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      // First Page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Subsequent Pages
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Sector_Report_${stateSelected?.stateName || 'State'}.pdf`);
    } catch (error) {
      console.error('PDF Export Error:', error);
      openNotification({ type: 'error', message: 'Failed to generate PDF.' });
    } finally {
      setLoading(false);
    }
  };

  const matrixData = getSectorMatrix();
  const overallPieData = getOverallPieData();

  // Graphical Tab Component
  const renderGraphicalOverview = () => (
    <Paper
      ref={chartRef}
      elevation={0}
      variant="outlined"
      sx={{
        p: 3,
        bgcolor: '#ffffff',
        maxWidth: '1100px',
        mx: 'auto',
        borderRadius: 1.5,
      }}
    >
      <Box textAlign="center" sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: theme.palette.primary.dark, textTransform: 'uppercase' }}
        >
          Overall Sector Share - {stateSelected?.stateName} State
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Graphical representation of industry proportion by sector
        </Typography>
      </Box>

      <Box sx={{ width: '100%', height: 400, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={overallPieData}
              cx="50%"
              cy="50%"
              outerRadius={120}
              dataKey="value"
              nameKey="name"
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
            >
              {overallPieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ height: '90vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        {/* Main Display Area */}
        <Grid2
          size={{ lg: 9, md: 8, sm: 12, xs: 12 }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: '#f8f9fa',
          }}
        >
          {/* Top Header Action Bar with View Tabs */}
          <Box
            sx={{
              px: 3,
              py: 0.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 48,
            }}
          >
            {/* View Mode Switcher */}
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              size="small"
              sx={{ minHeight: 38 }}
            >
              <Tab
                icon={<TableChartOutlined sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
                label="Full Report"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 38, fontSize: '0.8rem' }}
              />
              <Tab
                icon={<BarChartOutlined sx={{ fontSize: '1rem' }} />}
                iconPosition="start"
                label="Graphical Overview"
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 38, fontSize: '0.8rem' }}
              />
            </Tabs>

            {facilityList.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PictureAsPdfOutlined />}
                onClick={exportToPDF}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
              >
                Export PDF
              </Button>
            )}
          </Box>

          {/* Report Scrollable Workspace */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
            {loading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Generating Sector Analysis Report...
                </Typography>
              </Stack>
            ) : facilityList.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: '100%', color: 'text.secondary' }}
              >
                <Typography variant="h6">No Sector Report Generated</Typography>
                <Typography variant="body2">
                  Select a State in the right controls panel and click "Preview".
                </Typography>
              </Stack>
            ) : activeTab === 1 ? (
              renderGraphicalOverview()
            ) : (
              /* Printable Report Workspace */
              <Box
                id="report-pdf-container"
                ref={reportRef}
                sx={{
                  maxWidth: '1100px',
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {/* Master Summary Card with Single Matrix Table & Overall Pie Chart */}
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{ p: 3, bgcolor: '#ffffff', borderRadius: 1.5 }}
                >
                  {/* Clean Document Title */}
                  <Box
                    textAlign="center"
                    sx={{ mb: 2.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Typography
                      className="report-title"
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        color: theme.palette.primary.dark,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      Number of Industries in {stateSelected?.stateName} State by Sectors
                    </Typography>
                  </Box>

                  {/* Single Master Matrix Table */}
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 1, mb: 3 }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }}>
                            SECTOR
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }} align="center">
                            NUMBER OF INDUSTRIES
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }} align="center">
                            SUPER
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }} align="center">
                            LARGE
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }} align="center">
                            MEDIUM
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }} align="center">
                            SMALL
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.725rem' }} align="center">
                            MINI
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {matrixData.map((row) => (
                          <TableRow
                            key={row.sectorName}
                            sx={{
                              '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.725rem', py: 0.6 }}>
                              {row.sectorName}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 700, fontSize: '0.725rem', py: 0.6 }}
                            >
                              {row.total}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.725rem', py: 0.6 }}>
                              {row.SUPER || ''}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.725rem', py: 0.6 }}>
                              {row.LARGE || ''}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.725rem', py: 0.6 }}>
                              {row.MEDIUM || ''}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.725rem', py: 0.6 }}>
                              {row.SMALL || ''}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.725rem', py: 0.6 }}>
                              {row.MINI || ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Overall Sector Pie Chart */}
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      align="center"
                      display="block"
                      sx={{ mb: 1, textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      Overall Sector Industry Proportion
                    </Typography>
                    <Box sx={{ width: '100%', height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={overallPieData}
                            cx="50%"
                            cy="50%"
                            outerRadius={85}
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) =>
                              `${name} (${(percent * 100).toFixed(0)}%)`
                            }
                          >
                            {overallPieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={CHART_COLORS[index % CHART_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Paper>

                {/* Section-by-Section Category Breakdown Pie Charts */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="primary.dark"
                    sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Category Breakdowns by Sector
                  </Typography>

                  <Grid2 container spacing={2}>
                    {matrixData.map((sectorRow) => {
                      const sectorCatData = getSectorCategoryPieData(sectorRow);

                      return (
                        <Grid2 key={sectorRow.sectorName} size={{ xs: 12, sm: 6 }}>
                          <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 1.5, height: '100%' }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              align="center"
                              display="block"
                              sx={{
                                py: 0.5,
                                px: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main',
                                borderRadius: 1,
                                textTransform: 'uppercase',
                                mb: 1,
                              }}
                            >
                              {sectorRow.sectorName} Sector
                            </Typography>

                            <Box sx={{ width: '100%', height: 200 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={sectorCatData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={65}
                                    dataKey="value"
                                    nameKey="name"
                                    label={({ name, value }) => `${name}: ${value}`}
                                  >
                                    {sectorCatData.map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                                </PieChart>
                              </ResponsiveContainer>
                            </Box>
                          </Paper>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Box>
              </Box>
            )}
          </Box>
        </Grid2>

        {/* Right Sidebar Controls */}
        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '100%' }}>
            {/* Control Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Preview Controls
                </Typography>
              </Stack>
            </Box>

            {/* Selectors Form */}
            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  options={stateList}
                  loading={stateLoading}
                  getOptionLabel={(option) => option.stateName || ''}
                  value={stateSelected.stateId ? stateSelected : null}
                  onChange={(_, value) => setStateSelected(value || initState)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {stateLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Action Footer */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <VisibilityOutlined />
                }
                disabled={loading || !stateSelected?.stateId}
                onClick={handlePreviewReport}
                sx={{
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              >
                Preview
              </Button>
            </Box>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}
